import { formatDate, formatDateToSlash, formatMedicalPeriod, formatNumber, formateDateToKorean } from 'shared/lib';

import {
  footerContentTemplate,
  htmlFooter,
  htmlHeader,
  pageContentTemplate,
  singlePagefooterContentTemplate,
} from './detailTemplate';

/**
 * 기본 정보를 템플릿에 바인딩하는 함수
 * @param {string} template - 템플릿 문자열
 * @param {Object} data - I_24_DATA 객체
 * @param {number} currentPage - 현재 페이지 번호
 * @param {number} totalPages - 전체 페이지 수
 * @returns {string} 바인딩된 HTML 문자열
 */
const bindBasicInfo = (template, data, currentPage = 1, totalPages = 1) => {
  if (!data) return template;

  let result = template;

  // 기본 정보 바인딩
  const bindings = {
    med_dt: formatDate(data.med_dt) || '',
    dept_nm: data.dept_nm || '',
    medr_nm: data.medr_nm || '',
    medr_cd: data.medr_cd || '',
    pme_pse_cls_cd: data.pme_pse_cls_cd || '',
    pt_no: data.pt_no || '',
    pt_nm: data.pt_nm || '',
    med_period: formatMedicalPeriod(data.med_str_dt, data.med_end_dt),
    ward_room: data.ward_room || '',
    pt_gb: data.pt_gb || '',
    remark: data.remark || '',
    rpst_nm: data.rpst_nm || '',
    apply_dt: formateDateToKorean(data.apply_dt) || '',
    rcorg_no: data.rcorg_no || '',
    detail_statement_type: data.detail_statement_type || '',
    print_dt: formatDateToSlash(data.apply_dt),
  };

  // 템플릿의 name 속성에 해당하는 값들을 바인딩
  Object.keys(bindings).forEach((key) => {
    const tdRegex = new RegExp(`name="${key}"></td>`, 'g');
    result = result.replace(tdRegex, `name="${key}">${bindings[key]}</td>`);

    // span 태그 처리
    const spanRegex = new RegExp(`<span name="${key}"></span>`, 'g');
    result = result.replace(spanRegex, `<span name="${key}">${bindings[key]}</span>`);
  });

  // 페이지 정보 업데이트
  result = result.replace(
    /<span name="page_info">.*?<\/span>/,
    `<span name="page_info">${currentPage}/${totalPages}</span>`,
  );

  return result;
};

/**
 * 단일 페이지의 테이블 HTML 생성
 * @param {Array} items - 해당 페이지의 아이템들
 * @returns {string} 테이블 HTML 문자열
 */
const generateTableBody = (items) => {
  return items
    .map((item) => {
      // 소계, 계, 합계 등의 특별한 행은 스타일 클래스 추가
      const categoryWithoutSpaces = item.category.replace(/\s/g, '');
      const isTotalRowFirst = ['끝수처리조정금액'].includes(categoryWithoutSpaces);
      const isTotalRowLast = ['합계'].includes(categoryWithoutSpaces);

      const rowClass = isTotalRowFirst ? ' class="total-row-first"' : isTotalRowLast ? ' class="total-row-last"' : '';

      return `
      <tr${rowClass}>
        <td style="white-space: nowrap; width: fit-content;">${item.category || ''}</td>
        <td>${item.item_dt || ''}</td>
        <td style="width: fit-content;">${item.item_cd || ''}</td>
        <td style="white-space: nowrap; width:200px; min-width:200px; max-width:200px; overflow: hidden;">${item.item_nm || ''}</td>
        <td>${formatNumber(item.price)}</td>
        <td style="width:25px; min-width:25px; max-width:25px;">${item.cnt || ''}</td>
        <td style="width:25px; min-width:25px; max-width:25px;">${item.day_cnt || ''}</td>
        <td>${formatNumber(item.total_price)}</td>
        <td>${formatNumber(item.partial_patient_responsibility_price)}</td>
        <td>${formatNumber(item.insurance_price)}</td>
        <td>${formatNumber(item.full_patient_responsibility_price)}</td>
        <td>${formatNumber(item.uninsurance_price)}</td>
      </tr>
    `;
    })
    .join('');
};

/**
 * 페이지별 콘텐츠 생성
 * @param {Object} data - I_24_DATA 객체
 * @param {number} pageNumber - 페이지 번호 (1부터 시작)
 * @param {number} itemsPerPage - 페이지당 아이템 수
 * @returns {string} 해당 페이지의 콘텐츠 HTML 문자열
 */
const generatePageContent = (data, pageNumber, itemsPerPage = 25) => {
  if (!data || !data.detail_statement_items) return pageContentTemplate;

  const items = data.detail_statement_items;
  const totalItems = items.length;

  // dividerRow가 있는 경우 첫 페이지는 itemsPerPage-1개, 나머지는 itemsPerPage개
  const hasDividerRow = data.medr_cd && pageNumber === 1;
  const adjustedItemsPerPage = hasDividerRow ? itemsPerPage - 1 : itemsPerPage;

  const initPages = Math.ceil(totalItems / itemsPerPage);
  const totalPages = (totalItems % itemsPerPage) / itemsPerPage > 0.4 ? initPages + 1 : initPages;

  // 현재 페이지의 아이템들 추출 (dividerRow 고려하여 조정)
  let startIndex, endIndex;

  if (hasDividerRow) {
    // 첫 페이지에 dividerRow가 있는 경우
    startIndex = 0;
    endIndex = Math.min(adjustedItemsPerPage, totalItems);
  } else {
    // 첫 페이지가 아니거나 dividerRow가 없는 경우
    // dividerRow가 있었던 첫 페이지의 아이템 수를 고려하여 인덱스 계산
    const firstPageItems = data.medr_cd ? itemsPerPage - 1 : itemsPerPage;
    startIndex = firstPageItems + (pageNumber - 2) * itemsPerPage;
    endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  }

  const currentPageItems = items.slice(startIndex, endIndex);

  // 기본 정보 바인딩
  let template = bindBasicInfo(pageContentTemplate, data, pageNumber, totalPages);

  // 테이블 본문 생성
  const tableBodyHtml = generateTableBody(currentPageItems);

  // 테이블 본문 교체
  template = template.replace('<tbody name="table_body">', '<tbody>');

  // divider-row HTML 생성
  const dividerRowHtml = `
    <tr class="divider-row">
        <td name="med_dt">${formatDate(data.med_dt) || ''}</td>
        <td name="pme_pse_cls_cd">${data.pme_pse_cls_cd || ''}</td>
        <td colspan="10">
          <span name="dept_nm">${data.dept_nm || ''}</span>
          <span name="medr_nm">${data.medr_nm || ''}</span>
          (${data.medr_cd || ''})
        </td>
    </tr>`;

  // 첫 페이지이고 medr_cd가 있으면 divider-row 추가
  if (pageNumber === 1 && data.medr_cd) {
    template = template.replace('<!-- 테이블 내용이 여기에 들어갑니다 -->', dividerRowHtml + tableBodyHtml);
  } else {
    template = template.replace('<!-- 테이블 내용이 여기에 들어갑니다 -->', tableBodyHtml);
  }

  // 마지막 페이지가 아니면 footer 제거
  if (pageNumber < totalPages) {
    template = template.replace('<div name="footer_content">', '');
    template = template.replace('<!-- 푸터 내용이 여기에 들어갑니다 -->', '');
    template = template.replace('</div>', '');
  } else {
    // 마지막 페이지면 footer 콘텐츠 추가
    const footerContent = bindBasicInfo(footerContentTemplate, data, pageNumber, totalPages);
    template = template.replace('<div name="footer_content">', '');
    template = template.replace('<!-- 푸터 내용이 여기에 들어갑니다 -->', footerContent);
    template = template.replace('</div>', '');

    //마지막 페이지일 때 row 가 하나도 없다면 footer만 표시
    if (currentPageItems.length === 0 && pageNumber === totalPages) {
      template = bindBasicInfo(singlePagefooterContentTemplate, data, pageNumber, totalPages);
    }
  }

  return template;
};

/**
 * 모든 페이지의 HTML 생성
 * @param {Object} data - I_24_DATA 객체
 * @param {number} itemsPerPage - 페이지당 아이템 수 (기본값: 25)
 * @returns {string} 완전한 HTML 문자열
 */
export const generateAllPages = (data, itemsPerPage = 25) => {
  if (!data || !data.detail_statement_items) {
    return htmlHeader + pageContentTemplate + htmlFooter;
  }

  const totalItems = data.detail_statement_items.length;
  const initPages = Math.ceil(totalItems / itemsPerPage);
  const totalPages = (totalItems % itemsPerPage) / itemsPerPage > 0.4 ? initPages + 1 : initPages;

  // 모든 페이지의 콘텐츠 생성
  const pageContents = [];
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
    pageContents.push(generatePageContent(data, pageNumber, itemsPerPage));
  }

  // 하나의 완전한 HTML로 조합
  const combinedContent = pageContents.join('');
  return htmlHeader + combinedContent + htmlFooter;
};

/**
 * HTML을 PDF 요청 형식으로 변환하는 함수
 * @param {string} html - 완전한 HTML 문자열
 * @returns {Object} PDF 요청 형식의 객체
 */
export const convertToPdfRequest = (html) => {
  if (!html) {
    return { html: '' };
  }

  return {
    html: html,
  };
};
